/**
 * Orchestra AI Platform - API Card Component
 * 
 * Card component for displaying API endpoints with drag-and-drop support
 * 
 * @fileoverview API card with metadata, actions, and drag functionality
 */

import PropTypes from 'prop-types';
import { ServerBadge } from '../ui/ServerBadge';
import { MethodBadge } from '../ui/MethodBadge';
import './APICard.css';

/**
 * @typedef {Object} APICardData
 * @property {string} id
 * @property {string} name
 * @property {string} description
 * @property {('chinese'|'english'|'maths'|'myclass')} server
 * @property {string} endpoint
 * @property {('GET'|'POST'|'PUT'|'DELETE')} method
 * @property {string} category
 * @property {string[]} tags
 * @property {Object[]} parameters
 * @property {Object} response_schema
 * @property {boolean} [is_favorite]
 * @property {number} [usage_count]
 * @property {number} [estimated_duration]
 */

/**
 * API Card Component
 * @param {Object} props
 * @param {APICardData} props.data - API data
 * @param {Function} [props.onDrag] - Drag handler
 * @param {Function} [props.onFavorite] - Favorite toggle handler
 * @param {Function} [props.onTest] - Test handler
 * @param {Function} [props.onInfo] - Info handler
 */
export function APICard({ data, onDrag, onFavorite, onTest, onInfo }) {
  /**
   * Handle drag start
   * @param {React.DragEvent} event
   */
  const handleDragStart = (event) => {
    event.dataTransfer.setData('application/reactflow', JSON.stringify(data));
    event.dataTransfer.effectAllowed = 'move';
    if (onDrag) {
      onDrag(event, data);
    }
  };

  /**
   * Handle favorite click
   * @param {React.MouseEvent} event
   */
  const handleFavorite = (event) => {
    event.stopPropagation();
    if (onFavorite) {
      onFavorite(data);
    }
  };

  /**
   * Handle test click
   * @param {React.MouseEvent} event
   */
  const handleTest = (event) => {
    event.stopPropagation();
    if (onTest) {
      onTest(data);
    }
  };

  /**
   * Handle info click
   * @param {React.MouseEvent} event
   */
  const handleInfo = (event) => {
    event.stopPropagation();
    if (onInfo) {
      onInfo(data);
    }
  };

  return (
    <div
      className="api-card"
      draggable
      onDragStart={handleDragStart}
    >
      {/* Header */}
      <div className="api-card-header">
        <div className="api-card-badges">
          <ServerBadge server={data.server} size="sm" />
          <MethodBadge method={data.method} size="sm" />
        </div>
        <button
          onClick={handleFavorite}
          className="api-card-favorite"
          aria-label={data.is_favorite ? 'Unfavorite' : 'Favorite'}
        >
          {data.is_favorite ? '⭐' : '☆'}
        </button>
      </div>

      {/* Content */}
      <h3 className="api-card-title">{data.name}</h3>
      <p className="api-card-description">{data.description}</p>

      {/* Endpoint */}
      <code className="api-card-endpoint" title={data.endpoint}>
        {data.endpoint}
      </code>

      {/* Tags */}
      {data.tags && data.tags.length > 0 && (
        <div className="api-card-tags">
          {data.tags.slice(0, 3).map((tag, index) => (
            <span key={index} className="api-card-tag">
              {tag}
            </span>
          ))}
          {data.tags.length > 3 && (
            <span className="api-card-tag" style={{ color: '#9ca3af' }}>
              +{data.tags.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Metadata */}
      <div className="api-card-metadata">
        {data.usage_count !== undefined && (
          <span className="api-card-metadata-item">
            📊 {data.usage_count}
          </span>
        )}
        {data.estimated_duration && (
          <span className="api-card-metadata-item">
            ⚡ ~{data.estimated_duration}ms
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="api-card-actions">
        <button
          className="api-card-button"
          onClick={handleTest}
        >
          Test
        </button>
        <button
          className="api-card-button"
          onClick={handleInfo}
        >
          Info
        </button>
        <div className="api-card-spacer" />
        <span className="api-card-drag-handle" title="Drag to canvas">
          ⋮⋮
        </span>
      </div>
    </div>
  );
}

APICard.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    server: PropTypes.oneOf(['chinese', 'english', 'maths', 'myclass']).isRequired,
    endpoint: PropTypes.string.isRequired,
    method: PropTypes.oneOf(['GET', 'POST', 'PUT', 'DELETE']).isRequired,
    category: PropTypes.string.isRequired,
    tags: PropTypes.arrayOf(PropTypes.string),
    parameters: PropTypes.array,
    response_schema: PropTypes.object,
    is_favorite: PropTypes.bool,
    usage_count: PropTypes.number,
    estimated_duration: PropTypes.number,
  }).isRequired,
  onDrag: PropTypes.func,
  onFavorite: PropTypes.func,
  onTest: PropTypes.func,
  onInfo: PropTypes.func,
};

